import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormLabel,
  Typography,
  Button,
  FormHelperText,
  IconButton,
} from "@mui/material";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";

import { CreateContractData } from "../../../types/service.type";

import MilestoneField from "./MilestoneField";

const MilestoneDetails = () => {
  const { t } = useTranslation("goService", { keyPrefix: "createContractPage" });
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext<CreateContractData>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "milestones",
  });

  useEffect(() => {
    if (fields.length === 0) {
      append({
        name: "",
        deliveryDueDate: "",
        paymentDueDate: "",
      });
    }
  }, [append, fields.length]);

  const handleAddMilestone = () => {
    append({
      name: "",
      deliveryDueDate: "",
      paymentDueDate: "",
    });
  };

  const handleRemoveMilestone = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  const serviceType = watch("paymentConfig.type");

  return (
    <Box sx={styles.container}>
      <Typography sx={styles.title}>{t("milestoneDetails")}</Typography>
      <Box sx={styles.stepContainer}>
        <Box sx={{ ...styles.step }} />
        <Box sx={styles.step} />
      </Box>
      <Divider flexItem sx={styles.divider} />
      <Box sx={styles.serviceScheduleContainer}>
        <Typography sx={styles.serviceScheduleTitle}>{t("serviceSchedule")}</Typography>
        <Typography sx={styles.serviceScheduleDescription}>{t("eachServiceIsBrokenDownIntoMilestones")}</Typography>
      </Box>

      {fields.map((field, index) => (
        <Box key={field.id} sx={styles.milestoneContainer}>
          <Box sx={styles.milestoneHeader}>
            <MilestoneField index={index} serviceType={serviceType} />
          </Box>
        </Box>
      ))}

      {/* <Box sx={styles.addMilestoneContainer}>
        <Button onClick={handleAddMilestone} variant="text-sm">
          {t("addMilestone")}
        </Button>
        {fields.length > 1 && (
          <Button onClick={() => handleRemoveMilestone(fields.length - 1)} variant="outlined-sm">
            {t("removeMilestone")}
          </Button>
        )}
      </Box> */}

      <FormControl
        fullWidth
        component="fieldset"
        sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}
        error={!!errors?.termsAndConditions}
      >
        <Controller
          name="termsAndConditions"
          control={control}
          render={({ field }) => (
            <Box sx={{ ...styles.formControl }}>
              <Checkbox {...field} sx={styles.checkbox} size="small" />
              <FormLabel sx={styles.formLabel}>{t("iAgreeToGoServiceTermsAndConditions")}</FormLabel>
            </Box>
          )}
        />
        {errors?.termsAndConditions && <FormHelperText error>{t(errors?.termsAndConditions?.message)}</FormHelperText>}
      </FormControl>
    </Box>
  );
};

export default MilestoneDetails;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-start",
    gap: "20px",
  },
  title: {
    color: "var(--Gray-900, #101828)",
    fontFamily: "Montserrat",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  stepContainer: {
    width: "100%",
    display: "flex",
    gap: "8px",
    paddingBottom: "16px",
  },
  step: {
    flex: 1,
    borderTop: "4px solid var(--Brand-600, #008B07)",
  },
  divider: {
    mx: "-20px",
  },
  serviceScheduleTitle: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
  },
  serviceScheduleDescription: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "20px",
  },
  serviceScheduleContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  formControl: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "8px",
  },
  formLabel: {
    color: "var(--Dark---600, #707070) !important",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "20px",
  },
  checkbox: {
    p: 0,
  },
  milestoneContainer: {
    width: "100%",
  },
  milestoneHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
  },
  removeButton: {
    color: "error.main",
    mt: 2,
  },
  addMilestoneContainer: {
    display: "flex",
    flexDirection: "row",
    gap: "8px",
  },
};
